import { APP_STATE_VERSION } from '@/repositories/app-state'
import {
  assertMutationId,
  cloneWorkspaceDirectory,
  type WorkspaceCommitResult,
  type WorkspaceDirectory,
  type WorkspaceListener,
  type WorkspaceMutation,
  type WorkspaceRepository,
} from './WorkspaceRepository'

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export class WorkspaceApiError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly correlationId?: string,
  ) {
    super(message)
    this.name = 'WorkspaceApiError'
  }
}

export interface HttpWorkspaceAdapterOptions {
  baseUrl?: string
  workspaceId?: string
  authMode?: 'demo' | 'proxy'
  fetchImpl?: FetchLike
  broadcastChannel?: BroadcastChannel | null
}

const INVALIDATION_KIND = 'openelp-http-workspace-invalidated'

function requireWorkspaceDirectory(value: unknown): WorkspaceDirectory {
  if (!value || typeof value !== 'object') throw new TypeError('Workspace Data API returned an invalid snapshot')
  const snapshot = value as Partial<WorkspaceDirectory>
  if (!Number.isSafeInteger(snapshot.revision) || Number(snapshot.revision) < 0
      || !Array.isArray(snapshot.studentIndex)
      || !snapshot.workspaces || typeof snapshot.workspaces !== 'object' || Array.isArray(snapshot.workspaces)) {
    throw new TypeError('Workspace Data API returned an invalid snapshot')
  }
  return cloneWorkspaceDirectory(snapshot as WorkspaceDirectory)
}

function requireCommitResult(value: unknown, mutationId: string, expectedRevision: number): WorkspaceCommitResult {
  if (!value || typeof value !== 'object') throw new TypeError('Workspace Data API returned an invalid commit result')
  const result = value as Partial<WorkspaceCommitResult>
  if (!['committed', 'duplicate', 'conflict'].includes(String(result.status))
      || result.mutationId !== mutationId
      || !Number.isSafeInteger(result.revision)) {
    throw new TypeError('Workspace Data API returned an invalid commit result')
  }
  const snapshot = requireWorkspaceDirectory(result.snapshot)
  if (result.status === 'conflict') {
    return {
      status: 'conflict', mutationId, expectedRevision,
      revision: Number(result.revision), snapshot,
    }
  }
  return {
    status: result.status as 'committed' | 'duplicate',
    mutationId,
    revision: Number(result.revision), snapshot,
  }
}

export class HttpWorkspaceAdapter implements WorkspaceRepository {
  private readonly baseUrl: string
  private readonly workspaceId: string
  private readonly authMode: 'demo' | 'proxy'
  private readonly fetchImpl: FetchLike
  private readonly listeners = new Set<WorkspaceListener>()
  private readonly channel?: BroadcastChannel
  private snapshot: WorkspaceDirectory | null = null

  constructor(options: HttpWorkspaceAdapterOptions = {}) {
    this.baseUrl = (options.baseUrl ?? import.meta.env?.VITE_OPENELP_DATA_API_BASE_URL ?? '').replace(/\/$/, '')
    this.workspaceId = (options.workspaceId ?? import.meta.env?.VITE_OPENELP_WORKSPACE_ID ?? 'default').trim()
    if (!this.workspaceId) throw new TypeError('VITE_OPENELP_WORKSPACE_ID must not be empty')
    const authMode = options.authMode ?? import.meta.env?.VITE_OPENELP_DATA_AUTH_MODE ?? 'demo'
    if (authMode !== 'demo' && authMode !== 'proxy') throw new TypeError('VITE_OPENELP_DATA_AUTH_MODE must be demo or proxy')
    this.authMode = authMode
    this.fetchImpl = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init))
    this.channel = options.broadcastChannel === null
      ? undefined
      : options.broadcastChannel ?? (
          typeof BroadcastChannel === 'undefined'
            ? undefined
            : new BroadcastChannel(`openelp-http-workspace:${this.workspaceId}`)
        )
    this.channel?.addEventListener('message', (event) => {
      if (event.data?.kind !== INVALIDATION_KIND) return
      void this.load()
        .then((snapshot) => this.publish(snapshot))
        .catch((error) => console.warn('Workspace refresh failed after cross-tab invalidation', error))
    })
  }

  async load(): Promise<WorkspaceDirectory> {
    const response = await this.request('/api/workspace')
    const body = await response.json() as { workspace?: unknown }
    const snapshot = requireWorkspaceDirectory(body.workspace)
    this.snapshot = snapshot
    return cloneWorkspaceDirectory(snapshot)
  }

  async transact(expectedRevision: number, mutationId: string, mutation: WorkspaceMutation): Promise<WorkspaceCommitResult> {
    assertMutationId(mutationId)
    if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0) {
      throw new TypeError('Workspace expectedRevision must be a non-negative safe integer')
    }
    const current = this.snapshot ?? await this.load()
    if (current.revision !== expectedRevision) {
      return {
        status: 'conflict', mutationId, expectedRevision,
        revision: current.revision, snapshot: cloneWorkspaceDirectory(current),
      }
    }

    const draft = cloneWorkspaceDirectory(current)
    mutation(draft)
    const response = await this.request('/api/workspace/transactions', {
      method: 'POST',
      body: JSON.stringify({ expectedRevision, mutationId, appStateVersion: APP_STATE_VERSION, snapshot: draft }),
    }, true)
    const result = requireCommitResult(await response.json(), mutationId, expectedRevision)
    if (result.status !== 'duplicate') this.snapshot = cloneWorkspaceDirectory(result.snapshot)
    if (result.status === 'committed') {
      this.publish(result.snapshot)
      this.channel?.postMessage({ kind: INVALIDATION_KIND, revision: result.revision, mutationId })
    }
    return result
  }

  subscribe(listener: WorkspaceListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  async exportRecovery(): Promise<WorkspaceDirectory> {
    return this.load()
  }

  close(): void {
    this.channel?.close()
  }

  private async request(path: string, init: RequestInit = {}, acceptConflict = false): Promise<Response> {
    const headers = new Headers(init.headers)
    if (this.authMode === 'demo') headers.set('X-Workspace-Id', this.workspaceId)
    if (init.body !== undefined) headers.set('Content-Type', 'application/json')
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, { ...init, headers })
    if (response.ok || (acceptConflict && response.status === 409)) return response

    let detail = ''
    let apiError: WorkspaceApiError | null = null
    try {
      const body = await response.json() as {
        error?: unknown
      }
      if (typeof body.error === 'string') detail = `: ${body.error}`
      else if (body.error && typeof body.error === 'object') {
        const error = body.error as { code?: unknown; message?: unknown; correlationId?: unknown }
        const message = typeof error.message === 'string' ? error.message : ''
        const code = typeof error.code === 'string' ? error.code : undefined
        const correlationId = typeof error.correlationId === 'string' ? error.correlationId : undefined
        apiError = new WorkspaceApiError(
          `Workspace Data API request failed (${response.status})${message ? `: ${message}` : ''}`,
          code,
          correlationId,
        )
      }
    } catch {
      // The HTTP status remains the authoritative failure signal.
    }
    throw apiError ?? new Error(`Workspace Data API request failed (${response.status})${detail}`)
  }

  private publish(snapshot: WorkspaceDirectory): void {
    const published = cloneWorkspaceDirectory(snapshot)
    for (const listener of this.listeners) {
      try { listener(published) } catch { /* subscribers cannot undo a remote commit */ }
    }
  }
}
